import { FC, useState, useCallback } from 'react'
import {
  useDataProvider, useNotify, useTranslate,
} from 'react-admin'
import AddIcon from '@mui/icons-material/Add'
import CodeIcon from '@mui/icons-material/Code'
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Box, Button as MuiButton, Chip, Dialog, DialogContent, DialogTitle,
  Fade, FormControl, IconButton, InputLabel, MenuItem, Select,
  Stack, Switch, TextField as MuiTextField, Typography, alpha,
} from '@mui/material'
import { xxllncColor } from '../../layout/theme/color'
import { McpToolOut } from '../../types/apiRequests'

const BLUE = xxllncColor.dark100
const GREEN = xxllncColor.green100
const GRAY = 'rgba(0,0,0,0.45)'
const GRAY_BORDER = 'rgba(0,0,0,0.08)'

const inputSx = {
  '& .MuiOutlinedInput-root': {
    'borderRadius': '10px',
    'bgcolor': xxllncColor.base,
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BLUE },
  },
}

interface ToolEditDialogProps {
  tool: McpToolOut | null
  open: boolean
  onClose: () => void
  onSaved: () => void
}

const ToolEditDialog: FC<ToolEditDialogProps> = ({ tool, open, onClose, onSaved }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const translate = useTranslate()
  const [saving, setSaving] = useState(false)
  // eslint-disable-next-line camelcase
  const [form, setForm] = useState<{
    name: string
    description: string
    // eslint-disable-next-line camelcase
    http_method: string
    path: string
    parameters: Array<{
      name: string
      type: string
      description: string
      required: boolean
      enum?: string[]
    }>
    enabled: boolean
  }>({
    name: '',
    description: '',
    // eslint-disable-next-line camelcase
    http_method: 'GET',
    path: '',
    parameters: [],
    enabled: true,
  })

  const prevToolId = useState<string | null>(null)
  if (tool && String(tool.id) !== prevToolId[0]) {
    prevToolId[1](String(tool.id))
    setForm({
      name: tool.name,
      description: tool.description,
      // eslint-disable-next-line camelcase
      http_method: tool.http_method,
      path: tool.path,
      parameters: (tool.parameters ?? []).map((p: Record<string, unknown>) => ({
        name: (p.name as string) ?? '',
        type: (p.type as string) ?? 'string',
        description: (p.description as string) ?? '',
        required: (p.required as boolean) ?? false,
        enum: p.enum as string[] | undefined,
      })),
      enabled: tool.enabled,
    })
  }

  const handleSave = useCallback(async () => {
    if (!tool) return
    setSaving(true)
    try {
      await dataProvider.update('mcp_tool', {
        id: tool.id,
        data: {
          name: form.name,
          description: form.description,
          // eslint-disable-next-line camelcase
          http_method: form.http_method,
          path: form.path,
          parameters: form.parameters,
          enabled: form.enabled,
        },
        previousData: tool,
      })
      notify(translate('data.toolUpdated'), { type: 'success' })
      onSaved()
      onClose()
    } catch {
      notify(translate('data.toolSaveFailed'), { type: 'error' })
    } finally {
      setSaving(false)
    }
  }, [tool, form, dataProvider, notify, onSaved, onClose])

  const updateParam = (idx: number, field: string, value: unknown) => {
    setForm((prev) => ({
      ...prev,
      parameters: prev.parameters.map((p, i) => i === idx ? { ...p, [field]: value } : p),
    }))
  }

  const removeParam = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== idx),
    }))
  }

  const addParam = () => {
    setForm((prev) => ({
      ...prev,
      parameters: [...prev.parameters, { name: '', type: 'string', description: '', required: false }],
    }))
  }

  if (!tool) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: alpha(BLUE, 0.03), borderBottom: `1px solid ${GRAY_BORDER}`,
        py: 2, px: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CodeIcon sx={{ color: BLUE, fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '-0.01em' }}>
            {translate('data.toolEdit')}
          </Typography>
          {tool.is_modified && (
            <Chip label={translate('data.skillCustom')} size="small" sx={{
              height: 20, fontSize: '0.65rem', fontWeight: 600,
              bgcolor: alpha(BLUE, 0.1), color: BLUE, borderRadius: '6px',
            }} />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3, px: 3 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <MuiTextField
              label={translate('data.name')}
              size="small"
              fullWidth
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              sx={inputSx}
            />
            <FormControl size="small" sx={{ minWidth: 120, ...inputSx }}>
              <InputLabel>{translate('data.httpMethod')}</InputLabel>
              <Select
                // eslint-disable-next-line camelcase
                value={form.http_method}
                label={translate('data.httpMethod')}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    // eslint-disable-next-line camelcase
                    http_method: e.target.value,
                  }))
                }
              >
                {['GET', 'POST', 'PUT', 'DELETE'].map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <MuiTextField
            label={translate('data.path')}
            size="small"
            fullWidth
            value={form.path}
            onChange={(e) => setForm((p) => ({ ...p, path: e.target.value }))}
            sx={inputSx}
          />

          <MuiTextField
            label={translate('data.description')}
            size="small"
            fullWidth
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            sx={inputSx}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={form.enabled}
              onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: alpha(GREEN, 0.5) },
              }}
            />
            <Typography variant="body2" sx={{ color: form.enabled ? GREEN : GRAY, fontWeight: 600, fontSize: '0.82rem' }}>
              {form.enabled ? translate('data.toolEnabled') : translate('data.toolDisabled')}
            </Typography>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="caption" sx={{
                fontWeight: 700, color: GRAY, letterSpacing: '0.04em',
                textTransform: 'uppercase', fontSize: '0.65rem',
              }}>
                {translate('data.toolParameters')} ({form.parameters.length})
              </Typography>
              <MuiButton
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={addParam}
                sx={{
                  'textTransform': 'none', 'fontWeight': 600, 'fontSize': '0.75rem',
                  'color': BLUE, 'borderRadius': '8px',
                  '&:hover': { bgcolor: alpha(BLUE, 0.06) },
                }}
              >
                {translate('data.toolParameter')}
              </MuiButton>
            </Box>

            <Stack spacing={1.5}>
              {form.parameters.map((param, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 2, borderRadius: '12px',
                    border: `1px solid ${GRAY_BORDER}`,
                    bgcolor: 'rgba(0,0,0,0.015)',
                    position: 'relative',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => removeParam(idx)}
                    sx={{
                      'position': 'absolute', 'top': 8, 'right': 8,
                      'color': GRAY, 'opacity': 0.5,
                      '&:hover': { color: xxllncColor.red100, opacity: 1 },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>

                  <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                    <MuiTextField
                      label={translate('data.name')}
                      size="small"
                      fullWidth
                      value={param.name}
                      onChange={(e) => updateParam(idx, 'name', e.target.value)}
                      sx={inputSx}
                    />
                    <FormControl size="small" sx={{ minWidth: 110, ...inputSx }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={param.type}
                        label="Type"
                        onChange={(e) => updateParam(idx, 'type', e.target.value)}
                      >
                        {['string', 'integer', 'boolean', 'object'].map((t) => (
                          <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Switch
                      checked={param.required}
                      onChange={(e) => updateParam(idx, 'required', e.target.checked)}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                    <Typography variant="caption" sx={{ mt: 1.2, whiteSpace: 'nowrap', color: GRAY, fontSize: '0.72rem' }}>
                      {translate('data.required')}
                    </Typography>
                  </Box>

                  <MuiTextField
                    label={translate('data.description')}
                    size="small"
                    fullWidth
                    value={param.description}
                    onChange={(e) => updateParam(idx, 'description', e.target.value)}
                    sx={inputSx}
                  />

                  {param.enum && param.enum.length > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: GRAY, fontSize: '0.65rem', fontWeight: 600, mr: 0.5, mt: 0.3 }}>
                        {translate('data.toolEnum')}
                      </Typography>
                      {param.enum.map((v) => (
                        <Chip
                          key={v}
                          label={v}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.65rem', fontWeight: 500,
                            bgcolor: alpha(BLUE, 0.06), color: BLUE,
                            borderRadius: '5px',
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 1 }}>
            <MuiButton
              onClick={onClose}
              sx={{
                textTransform: 'none', fontWeight: 600, color: GRAY,
                borderRadius: '50px', px: 3,
              }}
            >
              {translate('data.skillCancel')}
            </MuiButton>
            <MuiButton
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => void handleSave()}
              disabled={saving || !form.name.trim()}
              disableElevation
              sx={{
                'bgcolor': BLUE,
                'color': xxllncColor.light100,
                'borderRadius': '50px',
                'textTransform': 'none',
                'fontWeight': 600,
                'px': 3,
                '&:hover': { bgcolor: BLUE, filter: 'brightness(0.9)' },
              }}
            >
              {translate('data.skillSave')}
            </MuiButton>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default ToolEditDialog
